/**
 * @fileoverview EditAttachmentsDialog
 *
 * Lets the user add, swap, or skip individual attachment slots on an
 * existing day-one precedent row, without having to regenerate from the
 * wizard. Closes MOR-389 (Eddie deselected a bank notification + 505,
 * came back to send, and had no way to re-attach the form).
 *
 * The slot layout (and most of the per-source UI) mirrors the wizard's
 * Step 3 attachment review at `generate-precedents.tsx:3404-3635`; the
 * resulting array is saved via `updatePrecedentAttachments`, which
 * mirrors the back-fill logic from `generatePrecedents`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useUploadFiles } from "@xixixao/uploadstuff/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { toast } from "sonner";
import {
  Check,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Loader2,
  Paperclip,
  Upload,
} from "lucide-react";

import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";
import type { JobPrecedentAttachment } from "@cvx/jobs/precedents/jobPrecedents/schema";
import type { DocumentCategory } from "@cvx/jobs/gatherInformation/documents/schema";
import type { PrecedentTemplateAttachment } from "@cvx/settings/precedents/precedentTemplates/schema";

import { Badge, Button } from "@/ui/primitives";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { Label } from "@/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/ui/composed/overlays";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";

import {
  buildAttachmentReferenceKey,
  buildAttachmentSlotKey,
  normaliseAttachmentRequirement,
  type AttachmentRequirement,
  type PrecedentAttachmentKey,
  type PrecedentAttachmentSourceKind,
} from "@/lib/precedent-attachments";
import { SharedReferencePicker } from "@/components/jobs/phases/day-one/SharedReferencePicker";

// ---------------------------------------------------------------------------
// Types — match the wizard's local AttachmentSelection so the slot UI and
// the final saved array share one shape.
// ---------------------------------------------------------------------------

type AttachmentSelection = {
  name: string;
  description?: string;
  required: boolean;
  attachmentKey: PrecedentAttachmentKey;
  sourceKind: PrecedentAttachmentSourceKind;
  status: "resolved" | "missing" | "skipped";
  documentCategory?: DocumentCategory;
  documentId?: Id<"documents">;
  generatedPrecedentId?: Id<"jobPrecedents">;
  generatedTemplateKey?: string;
  storageId?: Id<"_storage">;
  fileName?: string;
  fileType?: string;
  fileSize?: number;
  resolvedAt?: number;
};

type TemplateAttachmentFromDb = PrecedentTemplateAttachment;

type AttachmentCandidate = {
  documentId: Id<"documents">;
  storageId: Id<"_storage">;
  fileName: string;
  fileType: string;
  fileSize: number;
  category: DocumentCategory;
  documentType?: string;
  uploadedAt: number;
  downloadUrl: string | null;
  reason: string;
};

type SiblingPrecedent = {
  _id: Id<"jobPrecedents">;
  templateKey: string;
  name: string;
};

type PrecedentForEdit = {
  _id: Id<"jobPrecedents">;
  jobId: Id<"jobs">;
  orgId: Id<"organizations">;
  templateKey: string;
  name: string;
  attachments?: JobPrecedentAttachment[];
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function documentCategoryForAttachment(
  requirement: AttachmentRequirement,
): DocumentCategory {
  if (requirement.documentCategory) {
    return requirement.documentCategory as DocumentCategory;
  }
  switch (requirement.attachmentKey) {
    case "notice_of_appointment":
      return "notice_of_appointment";
    case "court_order":
      return "legal_disputes";
    case "rocap":
      return "director_disclosures";
    case "custom":
      return "other";
  }
}

/**
 * Strip an attachment down to the fields the `jobPrecedentAttachmentValidator`
 * accepts. The `precedent` prop comes from `listByJob`, which ENRICHES each
 * attachment with resolved URLs (`downloadUrl`, `generatedFilledPdfUrl`,
 * `generatedPrecedentName`); passing those extra fields back into
 * `updatePrecedentAttachments` fails Convex argument validation.
 */
function toStoredAttachment(
  att: JobPrecedentAttachment,
): JobPrecedentAttachment {
  return {
    name: att.name,
    description: att.description,
    required: att.required,
    attachmentKey: att.attachmentKey,
    customRef: att.customRef,
    sourceKind: att.sourceKind,
    status: att.status,
    documentCategory: att.documentCategory,
    documentId: att.documentId,
    generatedPrecedentId: att.generatedPrecedentId,
    generatedTemplateKey: att.generatedTemplateKey,
    storageId: att.storageId,
    fileName: att.fileName,
    fileType: att.fileType,
    fileSize: att.fileSize,
    resolvedAt: att.resolvedAt,
  };
}

/** Convert a stored attachment back into the wizard-shape selection. */
function attachmentToSelection(
  att: JobPrecedentAttachment,
): AttachmentSelection {
  return {
    name: att.name,
    description: att.description,
    required: att.required,
    attachmentKey: att.attachmentKey as PrecedentAttachmentKey,
    sourceKind: att.sourceKind as PrecedentAttachmentSourceKind,
    status: att.status,
    documentCategory: att.documentCategory as DocumentCategory | undefined,
    documentId: att.documentId,
    generatedPrecedentId: att.generatedPrecedentId,
    generatedTemplateKey: att.generatedTemplateKey,
    storageId: att.storageId,
    fileName: att.fileName,
    fileType: att.fileType,
    fileSize: att.fileSize,
    resolvedAt: att.resolvedAt,
  };
}

/**
 * Index stored attachments back into per-requirement slots and derive the
 * display/save order.
 *
 * Slot keys use the wizard's scheme — `attachmentKey:<requirement index>`
 * (the GLOBAL index over all requirements, matching how the wizard and this
 * dialog's save path build keys). Each stored attachment claims the first
 * unclaimed requirement slot it matches: exact match first (same key, and
 * same reference slug for `custom` / same name otherwise), then any slot
 * with the same `attachmentKey`.
 *
 * The returned `order` preserves the STORED array order (which is what the
 * download/merge pipeline uses), with never-stored requirement slots
 * appended in template order — so a user-arranged order survives reopening
 * the dialog.
 */
function indexExistingAttachments(
  requirements: AttachmentRequirement[],
  attachments: JobPrecedentAttachment[] | undefined,
): { selections: Record<string, AttachmentSelection>; order: string[] } {
  const selections: Record<string, AttachmentSelection> = {};
  const order: string[] = [];
  const slots = requirements.map((requirement, index) => ({
    requirement,
    slotKey: buildAttachmentSlotKey(requirement.attachmentKey, index),
    claimed: false,
  }));

  for (const att of attachments ?? []) {
    const slot =
      slots.find(
        (s) =>
          !s.claimed &&
          s.requirement.attachmentKey === att.attachmentKey &&
          (att.attachmentKey === "custom"
            ? buildAttachmentReferenceKey({
                attachmentKey: "custom",
                name: s.requirement.name,
                customRef: s.requirement.customRef,
              }) ===
              buildAttachmentReferenceKey({
                attachmentKey: "custom",
                name: att.name,
                customRef: att.customRef,
              })
            : s.requirement.name === att.name),
      ) ??
      slots.find(
        (s) => !s.claimed && s.requirement.attachmentKey === att.attachmentKey,
      );
    if (!slot) continue;
    slot.claimed = true;
    // A template-default slot stored as "skipped" was clobbered by the old
    // shared-reference propagation bug (a job-level skip used to overwrite
    // every same-named slot). Its default file still ships with the
    // template, so seed it back as resolved instead of freezing the skip.
    if (
      att.status === "skipped" &&
      slot.requirement.sourceKind === "template_default" &&
      (slot.requirement.storageId || slot.requirement.fileName)
    ) {
      selections[slot.slotKey] = {
        name: slot.requirement.name,
        description: slot.requirement.description,
        required: slot.requirement.required,
        attachmentKey: slot.requirement.attachmentKey,
        sourceKind: "template_default",
        status: "resolved",
        documentCategory: slot.requirement.documentCategory as
          DocumentCategory | undefined,
        storageId: slot.requirement.storageId as Id<"_storage"> | undefined,
        fileName: slot.requirement.fileName,
        fileType: slot.requirement.fileType,
        fileSize: slot.requirement.fileSize,
        resolvedAt: Date.now(),
      };
    } else {
      selections[slot.slotKey] = attachmentToSelection(att);
    }
    order.push(slot.slotKey);
  }

  for (const slot of slots) {
    if (slot.claimed) continue;
    order.push(slot.slotKey);
    // A template-default slot satisfies itself with the file that ships on
    // the template, so an unclaimed one (e.g. the template gained the
    // attachment after this precedent was generated) seeds as resolved
    // rather than missing — mirroring the wizard's resolution effect.
    const { requirement } = slot;
    if (
      requirement.sourceKind === "template_default" &&
      (requirement.storageId || requirement.fileName)
    ) {
      selections[slot.slotKey] = {
        name: requirement.name,
        description: requirement.description,
        required: requirement.required,
        attachmentKey: requirement.attachmentKey,
        sourceKind: "template_default",
        status: "resolved",
        documentCategory: requirement.documentCategory as
          DocumentCategory | undefined,
        storageId: requirement.storageId as Id<"_storage"> | undefined,
        fileName: requirement.fileName,
        fileType: requirement.fileType,
        fileSize: requirement.fileSize,
        resolvedAt: Date.now(),
      };
    }
  }

  return { selections, order };
}

function normaliseRequirements(
  raw: TemplateAttachmentFromDb[] | undefined,
): AttachmentRequirement[] {
  return (raw ?? []).map((attachment) => ({
    ...normaliseAttachmentRequirement({
      name: attachment.name,
      description: attachment.description,
      required: attachment.required,
    }),
    ...(attachment as object),
  })) as AttachmentRequirement[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EditAttachmentsDialog({
  precedent,
  dbTemplate,
  siblingPrecedents,
  open,
  onOpenChange,
}: {
  precedent: PrecedentForEdit;
  /** Resolved precedent template carrying `attachments` requirements. */
  dbTemplate: { attachments?: TemplateAttachmentFromDb[] } | null | undefined;
  /** Other precedents on the same job, used for the generated_precedent picker. */
  siblingPrecedents: SiblingPrecedent[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const requirements = useMemo(
    () => normaliseRequirements(dbTemplate?.attachments),
    [dbTemplate?.attachments],
  );

  // Selections state keyed by slotKey, seeded from the stored attachments,
  // plus the user-arrangeable slot order (drives display AND the order the
  // attachments merge into the downloaded document).
  const [selections, setSelections] = useState<
    Record<string, AttachmentSelection>
  >(
    () =>
      indexExistingAttachments(requirements, precedent.attachments).selections,
  );
  const [slotOrder, setSlotOrder] = useState<string[]>(
    () => indexExistingAttachments(requirements, precedent.attachments).order,
  );
  // Re-seed whenever the dialog re-opens against a different precedent so a
  // background mutation can't leave the editor showing stale state.
  useEffect(() => {
    if (!open) return;
    const seeded = indexExistingAttachments(
      requirements,
      precedent.attachments,
    );
    setSelections(seeded.selections);
    setSlotOrder(seeded.order);
  }, [open, precedent._id, precedent.attachments, requirements]);

  // Slot lookup for order-driven rendering/saving.
  const requirementBySlotKey = useMemo(() => {
    const map = new Map<
      string,
      { requirement: AttachmentRequirement; index: number }
    >();
    requirements.forEach((requirement, index) => {
      map.set(buildAttachmentSlotKey(requirement.attachmentKey, index), {
        requirement,
        index,
      });
    });
    return map;
  }, [requirements]);

  // Move a slot one position up (-1) or down (+1) in the save/display order.
  const moveSlot = useCallback((slotKey: string, delta: -1 | 1) => {
    setSlotOrder((prev) => {
      const from = prev.indexOf(slotKey);
      const to = from + delta;
      if (from === -1 || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[from], next[to]] = [next[to], next[from]];
      return next;
    });
  }, []);

  // Candidates from existing job documents (NOA, court orders, ROCAPs).
  const { data: attachmentCandidateGroups } = useQuery({
    ...convexQuery(
      api.jobs.precedents.attachments.resolveAttachmentCandidates,
      {
        jobId: precedent.jobId,
      },
    ),
    enabled: open,
  });

  const candidatesByKey = useMemo(() => {
    const map = new Map<PrecedentAttachmentKey, AttachmentCandidate[]>();
    for (const group of (attachmentCandidateGroups ?? []) as Array<{
      attachmentKey: PrecedentAttachmentKey;
      candidates: AttachmentCandidate[];
    }>) {
      map.set(group.attachmentKey, group.candidates);
    }
    return map;
  }, [attachmentCandidateGroups]);

  // Custom (named) attachments resolve from ANY uploaded job document and are
  // shared across precedents, so they use the job-level reference map.
  const { data: attachableJobDocuments } = useQuery({
    ...convexQuery(api.jobs.precedents.attachments.listAttachableJobDocuments, {
      jobId: precedent.jobId,
    }),
    enabled: open,
  });
  const { data: resolvedReferences } = useQuery({
    ...convexQuery(
      api.jobs.precedents.attachmentReferences.listResolvedReferences,
      { jobId: precedent.jobId },
    ),
    enabled: open,
  });
  const referenceCandidates = useMemo(
    () =>
      (attachableJobDocuments ?? []).map((doc) => ({
        documentId: doc.documentId,
        fileName: doc.fileName,
      })),
    [attachableJobDocuments],
  );
  const resolutionByRefKey = useMemo(() => {
    const map = new Map<
      string,
      {
        status: "resolved" | "skipped";
        documentId: Id<"documents"> | null;
        fileName: string | null;
      }
    >();
    for (const row of resolvedReferences ?? []) {
      map.set(row.referenceKey, {
        status: row.status,
        documentId: row.documentId,
        fileName: row.fileName,
      });
    }
    return map;
  }, [resolvedReferences]);

  // Upload plumbing — mirrors the wizard's `startAttachmentUpload`.
  const generateDocumentUploadUrl = useConvexMutation(
    api.jobs.gatherInformation.documents.mutations.generateUploadUrl,
  );
  const { mutateAsync: saveDocument } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.gatherInformation.documents.mutations.saveDocument,
    ),
  });
  const pendingUploadRef = useRef<{
    slotKey: string;
    requirement: AttachmentRequirement;
  } | null>(null);
  const [uploadingSlotKey, setUploadingSlotKey] = useState<string | null>(null);

  const { startUpload } = useUploadFiles(
    async () => await generateDocumentUploadUrl({ jobId: precedent.jobId }),
    {
      onUploadBegin: () => {
        const pending = pendingUploadRef.current;
        if (pending) setUploadingSlotKey(pending.slotKey);
      },
      onUploadComplete: async (uploaded) => {
        const pending = pendingUploadRef.current;
        if (!pending) return;
        const file = uploaded[0];
        if (!file) return;
        try {
          const storageId = (file.response as { storageId: Id<"_storage"> })
            .storageId;
          const category = documentCategoryForAttachment(pending.requirement);
          const documentId = await saveDocument({
            jobId: precedent.jobId,
            storageId,
            fileName: file.name,
            fileType: file.type || "application/octet-stream",
            fileSize: file.size,
            category,
          });
          setSelections((prev) => ({
            ...prev,
            [pending.slotKey]: {
              name: pending.requirement.name,
              description: pending.requirement.description,
              required: pending.requirement.required,
              attachmentKey: pending.requirement.attachmentKey,
              sourceKind: "upload",
              status: "resolved",
              documentCategory: category,
              documentId,
              storageId,
              fileName: file.name,
              fileType: file.type || "application/octet-stream",
              fileSize: file.size,
              resolvedAt: Date.now(),
            },
          }));
          toast.success(`${file.name} attached`);
        } catch (err) {
          toast.error(extractErrorMessage(err, "Failed to attach file"));
        } finally {
          pendingUploadRef.current = null;
          setUploadingSlotKey(null);
        }
      },
      onUploadError: (err: unknown) => {
        const msg = err instanceof Error ? err.message : "Unknown upload error";
        pendingUploadRef.current = null;
        setUploadingSlotKey(null);
        toast.error(`Attachment upload failed: ${msg}`);
      },
    },
  );

  const handleCandidateChange = useCallback(
    (
      slotKey: string,
      requirement: AttachmentRequirement,
      candidateId: string,
    ) => {
      const candidates = candidatesByKey.get(requirement.attachmentKey) ?? [];
      const candidate = candidates.find((c) => c.documentId === candidateId);
      if (!candidate) return;
      setSelections((prev) => ({
        ...prev,
        [slotKey]: {
          name: requirement.name,
          description: requirement.description,
          required: requirement.required,
          attachmentKey: requirement.attachmentKey,
          sourceKind: "job_document",
          status: "resolved",
          documentCategory: candidate.category,
          documentId: candidate.documentId,
          storageId: candidate.storageId,
          fileName: candidate.fileName,
          fileType: candidate.fileType,
          fileSize: candidate.fileSize,
          resolvedAt: Date.now(),
        },
      }));
    },
    [candidatesByKey],
  );

  const handleGeneratedPrecedentChange = useCallback(
    (
      slotKey: string,
      requirement: AttachmentRequirement,
      siblingId: Id<"jobPrecedents">,
    ) => {
      // Select by row id, not templateKey — a fanned-out template has one
      // row per contact, and matching by key would always link the first.
      const sibling = siblingPrecedents.find((s) => s._id === siblingId);
      if (!sibling) return;
      setSelections((prev) => ({
        ...prev,
        [slotKey]: {
          name: requirement.name,
          description: requirement.description,
          required: requirement.required,
          attachmentKey: requirement.attachmentKey,
          sourceKind: "generated_precedent",
          status: "resolved",
          generatedTemplateKey: sibling.templateKey,
          generatedPrecedentId: sibling._id,
          fileName: sibling.name,
          resolvedAt: Date.now(),
        },
      }));
    },
    [siblingPrecedents],
  );

  const handleSkip = useCallback(
    (slotKey: string, requirement: AttachmentRequirement) => {
      setSelections((prev) => ({
        ...prev,
        [slotKey]: {
          name: requirement.name,
          description: requirement.description,
          required: requirement.required,
          attachmentKey: requirement.attachmentKey,
          sourceKind: "none",
          status: "skipped",
          documentCategory: requirement.documentCategory as
            DocumentCategory | undefined,
        },
      }));
    },
    [],
  );

  const handleUpload = useCallback(
    (
      slotKey: string,
      requirement: AttachmentRequirement,
      file: File | null,
    ) => {
      if (!file) return;
      pendingUploadRef.current = { slotKey, requirement };
      startUpload([file]);
    },
    [startUpload],
  );

  const { mutateAsync: updateAttachments, isPending: saving } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.precedents.mutations.updatePrecedentAttachments,
    ),
  });

  const handleSave = useCallback(async () => {
    // Assemble the final attachments array in the user-arranged slot order —
    // the stored order is what the download/merge pipeline follows, so
    // dragging a slot up/down here changes the order of the appendices in
    // the final document. Missing slots are recorded with status="missing"
    // so the editor and badges can surface them consistently.
    const finalAttachments: JobPrecedentAttachment[] = [];
    slotOrder.forEach((slotKey) => {
      const slot = requirementBySlotKey.get(slotKey);
      if (!slot) return;
      const { requirement } = slot;
      // Custom (named) attachments are owned by the job-level reference map and
      // resolved by SharedReferencePicker (which already persisted + propagated
      // them). Take their value from the live precedent so this save never
      // reverts a just-applied reference; fall back to a missing slot.
      // Generated-precedent slots are NOT shared references even when custom-
      // named (MOR-779) — they save from the local selection below, otherwise
      // the user's picked sibling document was silently discarded on save.
      // Template-default and upload slots self-resolve too, so they also save
      // from the local selection.
      if (
        requirement.attachmentKey === "custom" &&
        requirement.sourceKind !== "generated_precedent" &&
        requirement.sourceKind !== "template_default" &&
        requirement.sourceKind !== "upload"
      ) {
        const refKey = buildAttachmentReferenceKey({
          attachmentKey: "custom",
          name: requirement.name,
          customRef: requirement.customRef,
        });
        const live = (precedent.attachments ?? []).find(
          (att) =>
            att.attachmentKey === "custom" &&
            buildAttachmentReferenceKey({
              attachmentKey: "custom",
              name: att.name,
              customRef: att.customRef,
            }) === refKey,
        );
        finalAttachments.push(
          live
            ? toStoredAttachment(live)
            : {
                name: requirement.name,
                description: requirement.description,
                required: requirement.required,
                attachmentKey: "custom",
                customRef: requirement.customRef,
                sourceKind: "job_document",
                status: "missing",
                documentCategory: requirement.documentCategory as
                  DocumentCategory | undefined,
              },
        );
        return;
      }
      const existing = selections[slotKey];
      if (existing) {
        finalAttachments.push({
          name: existing.name,
          description: existing.description,
          required: existing.required,
          attachmentKey: existing.attachmentKey,
          sourceKind: existing.sourceKind,
          status: existing.status,
          documentCategory: existing.documentCategory,
          documentId: existing.documentId,
          generatedPrecedentId: existing.generatedPrecedentId,
          generatedTemplateKey: existing.generatedTemplateKey,
          storageId: existing.storageId,
          fileName: existing.fileName,
          fileType: existing.fileType,
          fileSize: existing.fileSize,
          resolvedAt: existing.resolvedAt,
        });
      } else {
        finalAttachments.push({
          name: requirement.name,
          description: requirement.description,
          required: requirement.required,
          attachmentKey: requirement.attachmentKey,
          sourceKind: (requirement.sourceKind ||
            "none") as PrecedentAttachmentSourceKind,
          status: "missing",
          documentCategory: requirement.documentCategory as
            DocumentCategory | undefined,
        });
      }
    });

    try {
      await updateAttachments({
        precedentId: precedent._id,
        attachments: finalAttachments,
      });
      toast.success("Attachments updated");
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to update attachments"));
    }
  }, [
    slotOrder,
    requirementBySlotKey,
    selections,
    updateAttachments,
    precedent._id,
    precedent.attachments,
    onOpenChange,
  ]);

  const generatedPrecedentOptions = useMemo(
    () =>
      siblingPrecedents.filter((s) => s.templateKey !== precedent.templateKey),
    [siblingPrecedents, precedent.templateKey],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="size-4 text-primary/40" />
            Edit attachments
          </DialogTitle>
          <DialogDescription>
            Choose, swap, or skip each attachment for {precedent.name}.
          </DialogDescription>
        </DialogHeader>

        {requirements.length === 0 ? (
          <div className="rounded-md border border-border/60 bg-muted/30 px-4 py-6 text-center text-sm text-primary/60">
            This template has no configured attachment slots.
          </div>
        ) : (
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {slotOrder.map((slotKey, orderIndex) => {
              const slot = requirementBySlotKey.get(slotKey);
              if (!slot) return null;
              const { requirement } = slot;
              const selection = selections[slotKey];
              const candidates =
                candidatesByKey.get(requirement.attachmentKey) ?? [];
              const isUploading = uploadingSlotKey === slotKey;
              const selectedGenerated =
                generatedPrecedentOptions.find(
                  (s) => s._id === selection?.generatedPrecedentId,
                ) ??
                generatedPrecedentOptions.find(
                  (s) => s.templateKey === selection?.generatedTemplateKey,
                );

              return (
                <ReorderableAttachmentSlot
                  key={slotKey}
                  onMoveUp={
                    orderIndex > 0 ? () => moveSlot(slotKey, -1) : undefined
                  }
                  onMoveDown={
                    orderIndex < slotOrder.length - 1
                      ? () => moveSlot(slotKey, 1)
                      : undefined
                  }
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-primary/80">
                          {requirement.name}
                        </span>
                        {requirement.required && (
                          <Badge variant="secondary" className="text-[10px]">
                            Required
                          </Badge>
                        )}
                        {selection?.status === "resolved" ? (
                          <Badge
                            color="emerald"
                            variant="soft"
                            className="gap-1 text-[10px]"
                          >
                            <Check className="size-3" />
                            Attached
                          </Badge>
                        ) : selection?.status === "skipped" ? (
                          <Badge variant="secondary" className="text-[10px]">
                            Skipped
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="text-[10px]">
                            Missing
                          </Badge>
                        )}
                      </div>
                      {requirement.description && (
                        <p className="mt-1 text-xs text-primary/45">
                          {requirement.description}
                        </p>
                      )}
                    </div>
                    {selection?.fileName && (
                      <span className="max-w-[240px] truncate text-xs text-primary/50">
                        {selection.fileName}
                      </span>
                    )}
                  </div>

                  {requirement.sourceKind === "template_default" && (
                    <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200">
                      <span className="font-medium">
                        Template default file:
                      </span>{" "}
                      {selection?.fileName ??
                        requirement.fileName ??
                        "No default file configured"}
                    </div>
                  )}

                  {requirement.sourceKind === "generated_precedent" && (
                    <div className="mt-3">
                      <Label className="text-xs">Generated precedent</Label>
                      <Popover modal>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            className="mt-1 w-full justify-between overflow-hidden text-sm font-normal"
                          >
                            <span className="truncate text-left">
                              {selectedGenerated?.name ??
                                "Select generated document"}
                            </span>
                            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-[var(--radix-popover-trigger-width)] p-0"
                          align="start"
                          onWheel={(e) => e.stopPropagation()}
                        >
                          <Command onKeyDown={(e) => e.stopPropagation()}>
                            <CommandInput placeholder="Search templates..." />
                            <CommandList className="max-h-64 overscroll-contain">
                              <CommandEmpty>No matching template.</CommandEmpty>
                              {generatedPrecedentOptions.map((option) => (
                                <CommandItem
                                  key={option._id}
                                  value={`${option.name} ${option._id}`}
                                  onSelect={() =>
                                    handleGeneratedPrecedentChange(
                                      slotKey,
                                      requirement,
                                      option._id,
                                    )
                                  }
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-3 w-3 shrink-0",
                                      selectedGenerated?._id === option._id
                                        ? "opacity-100"
                                        : "opacity-0",
                                    )}
                                  />
                                  <span className="truncate">
                                    {option.name}
                                  </span>
                                </CommandItem>
                              ))}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      {generatedPrecedentOptions.length === 0 && (
                        <p className="mt-1 text-xs text-primary/45">
                          No other day-one documents exist on this job to link.
                        </p>
                      )}
                    </div>
                  )}

                  {requirement.attachmentKey === "custom" &&
                  requirement.sourceKind !== "generated_precedent" &&
                  requirement.sourceKind !== "template_default" &&
                  requirement.sourceKind !== "upload" ? (
                    <div className="mt-3 space-y-2">
                      <SharedReferencePicker
                        jobId={precedent.jobId}
                        referenceKey={buildAttachmentReferenceKey({
                          attachmentKey: "custom",
                          name: requirement.name,
                          customRef: requirement.customRef,
                        })}
                        name={requirement.name}
                        candidates={referenceCandidates}
                        resolution={
                          resolutionByRefKey.get(
                            buildAttachmentReferenceKey({
                              attachmentKey: "custom",
                              name: requirement.name,
                              customRef: requirement.customRef,
                            }),
                          ) ?? null
                        }
                      />
                      <p className="text-xs text-primary/45">
                        Shared reference — applies to every precedent that uses
                        it.
                      </p>
                    </div>
                  ) : (
                    <div className="mt-3 grid gap-2 md:grid-cols-[1fr_auto_auto]">
                      {requirement.sourceKind === "job_document" ||
                      requirement.sourceKind === "upload" ||
                      requirement.sourceKind === "none" ||
                      requirement.sourceKind === undefined ? (
                        <Select
                          value={selection?.documentId ?? ""}
                          onValueChange={(value) =>
                            handleCandidateChange(slotKey, requirement, value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                candidates.length > 0
                                  ? "Select existing job file"
                                  : "No matching job files"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {candidates.map((candidate) => (
                              <SelectItem
                                key={candidate.documentId}
                                value={candidate.documentId}
                              >
                                {candidate.fileName}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div />
                      )}

                      <label className="inline-flex cursor-pointer items-center justify-center rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-primary/70 hover:bg-secondary">
                        {isUploading ? (
                          <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                        ) : (
                          <Upload className="mr-1.5 size-3.5" />
                        )}
                        Upload
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          className="sr-only"
                          onChange={(event) => {
                            const file = event.target.files?.[0] ?? null;
                            handleUpload(slotKey, requirement, file);
                            event.currentTarget.value = "";
                          }}
                        />
                      </label>

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSkip(slotKey, requirement)}
                      >
                        Skip
                      </Button>
                    </div>
                  )}
                </ReorderableAttachmentSlot>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || requirements.length === 0}
          >
            {saving ? (
              <>
                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                Saving
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Wrapper for one attachment slot card with up/down reorder buttons. The
 * arranged order is saved as the stored attachments order, which is the
 * order attachments merge into the downloaded document. A missing handler
 * (first/last slot) renders that button disabled.
 */
function ReorderableAttachmentSlot({
  onMoveUp,
  onMoveDown,
  children,
}: {
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  children: ReactNode;
}) {
  const arrowClass = (enabled: boolean) =>
    cn(
      "rounded p-0.5",
      enabled
        ? "text-primary/40 hover:bg-secondary hover:text-primary/70"
        : "cursor-default text-primary/15",
    );
  return (
    <div className="flex items-start gap-2 rounded-md border border-border/50 bg-background p-3">
      <div className="mt-0.5 flex shrink-0 flex-col">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={!onMoveUp}
          className={arrowClass(!!onMoveUp)}
          aria-label="Move attachment up"
          title="Move up"
        >
          <ChevronUp className="size-4" />
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={!onMoveDown}
          className={arrowClass(!!onMoveDown)}
          aria-label="Move attachment down"
          title="Move down"
        >
          <ChevronDown className="size-4" />
        </button>
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
